import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { database, storage, auth } from './firebase';
import { ref, get, push, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { PET_STATUS, PET_TYPES, PET_SIZES } from './constants'; // Importamos las constantes

const AdminForm = () => {
  const { petId } = useParams();
  const navigate = useNavigate();
  const [pet, setPet] = useState({
    name: '',
    type: '',
    color: '',
    size: '',
    sex: '',
    age: '',
    status: '',
    description: '',
    notes: '',
    location: '',
    images: []
  });
  const [isLoading, setIsLoading] = useState(!!petId);
  const [error, setError] = useState(null);
  const [imagePreviews, setImagePreviews] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (petId) {
      const fetchPet = async () => {
        try {
          const petRef = ref(database, `pets/${petId}`);
          const snapshot = await get(petRef);
          if (snapshot.exists()) {
            const petData = snapshot.val();
            // Permitir editar si es el dueño O si la mascota no tiene dueño (fue subida por formulario público)
            if (petData.userId && auth.currentUser.uid !== petData.userId) {
              setError("No tienes permiso para editar esta mascota");
              return;
            }
            setPet(petData);
            setImagePreviews(petData.images || []);
          } else {
            setError("Mascota no encontrada");
          }
        } catch (err) {
          setError("Error al cargar los datos de la mascota");
        } finally {
          setIsLoading(false);
        }
      };

      fetchPet();
    }
  }, [petId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (petId && pet.userId && auth.currentUser.uid !== pet.userId) {
      setError("No tienes permiso para editar esta mascota");
      return;
    }

    try {
      let petToSave = { ...pet };
      let currentPetId = petId;

      // 1. Crear o identificar el ID de la mascota
      if (!currentPetId) {
        const newPetRef = push(ref(database, 'pets'));
        currentPetId = newPetRef.key;
        petToSave.userId = auth.currentUser.uid;
        petToSave.createdAt = Date.now();
      }

      // 2. Subir y gestionar imágenes
      const uploadImage = async (image) => {
        const imageName = `${petToSave.name.replace(/\s+/g, '-')}-${currentPetId}-${Date.now()}`;
        const imageRef = storageRef(storage, `pets/${imageName}`);
        const response = await fetch(image);
        const blob = await response.blob();
        await uploadBytes(imageRef, blob);
        return await getDownloadURL(imageRef);
      };

      const newImageUrls = await Promise.all(
        imagePreviews
          .filter(p => !p.startsWith('https')) // Filtrar solo las nuevas imágenes (base64)
          .map(uploadImage)
      );

      const existingImages = imagePreviews.filter(p => p.startsWith('https'));

      // Eliminar imágenes antiguas que ya no están (solo si estamos editando)
      if (petId && pet.images && pet.images.length > 0) {
        const imagesToDelete = pet.images.filter(img => !existingImages.includes(img));
        for (const imageUrl of imagesToDelete) {
          try {
            const imageRefToDelete = storageRef(storage, imageUrl);
            await deleteObject(imageRefToDelete);
          } catch (error) {
            // Si la imagen no existe en storage (por ej, si es una URL de un objeto que ya no existe), no hacer nada
            if (error.code !== 'storage/object-not-found') {
              throw error;
            }
          }
        }
      }

      petToSave.images = [...existingImages, ...newImageUrls];

      // 3. Guardar/Actualizar la mascota en la base de datos
      const petRef = ref(database, `pets/${currentPetId}`);
      await set(petRef, petToSave);

      alert(petId ? "Mascota actualizada con éxito" : "Mascota agregada exitosamente");
      navigate('/');

    } catch (err) {
      setError(petId ? "Error al actualizar la mascota" : "Error al agregar la mascota");
      console.log("error "+err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPet(prevPet => ({ ...prevPet, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (imagePreviews.length + files.length > 4) {
      alert('Solo se permiten un máximo de 4 imágenes');
      return;
    }

    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index) => {
    setImagePreviews(prevPreviews => prevPreviews.filter((_, i) => i !== index));
  };

  if (isLoading) return <div className="text-center">Cargando...</div>;
  if (error) return <div className="text-red-500 text-center">{error}</div>;

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-lg mx-auto bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
      <h2 className="text-2xl font-bold text-center text-blue-600">
        {petId ? (pet.active === false ? "Revisar Solicitud Pública" : "Editar Mascota") : "Agregar Nueva Mascota"}
      </h2>

      {pet.rescuerName && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
          <h3 className="font-bold text-blue-800 mb-2">Datos del Rescatista:</h3>
          <p className="text-sm"><strong>Nombre:</strong> {pet.rescuerName}</p>
          <p className="text-sm"><strong>Teléfono:</strong> {pet.rescuerPhone}</p>
          <p className="text-sm"><strong>Email:</strong> {pet.rescuerEmail}</p>
        </div>
      )}
      
      {pet.active === false && (
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-6 flex flex-col items-center">
          <p className="text-yellow-800 font-semibold mb-3">Esta mascota está pendiente de activación</p>
          <button
            type="button"
            onClick={() => {
              setPet(prev => ({ ...prev, active: true, userId: auth.currentUser.uid }));
              alert("Mascota marcada como activa. Al guardar, aparecerá en la lista pública y serás el administrador responsable.");
            }}
            className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-full shadow-md transition-all"
          >
            ✅ Activar y Tomar Responsabilidad
          </button>
        </div>
      )}

      <div>
        <label htmlFor="name" className="block text-gray-700 text-sm font-bold mb-2">Nombre de la mascota</label>
        <input
          type="text"
          id="name"
          name="name"
          value={pet.name}
          onChange={handleChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        />
      </div>

      <div>
        <label htmlFor="type" className="block text-gray-700 text-sm font-bold mb-2">Tipo de mascota</label>
        <select
          id="type"
          name="type"
          value={pet.type}
          onChange={handleChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        >
          <option value="">Seleccione el tipo</option>
          {Object.values(PET_TYPES).map(type => (
            <option key={type.value} value={type.value}>
              {type.icon && `${type.icon} `}{type.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="color" className="block text-gray-700 text-sm font-bold mb-2">Color</label>
        <input
          type="text"
          id="color"
          name="color"
          value={pet.color}
          onChange={handleChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        />
      </div>

      <div>
        <label htmlFor="size" className="block text-gray-700 text-sm font-bold mb-2">Tamaño</label>
        <select
          id="size"
          name="size"
          value={pet.size}
          onChange={handleChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        >
          <option value="">Seleccione el tamaño</option>
          {Object.values(PET_SIZES).map(size => (
            <option key={size.value} value={size.value}>
              {size.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="sex" className="block text-gray-700 text-sm font-bold mb-2">Sexo</label>
        <select
          id="sex"
          name="sex"
          value={pet.sex}
          onChange={handleChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        >
          <option value="">Seleccione el sexo</option>
          <option value="male">Macho</option>
          <option value="female">Hembra</option>
        </select>
      </div>

      <div>
        <label htmlFor="age" className="block text-gray-700 text-sm font-bold mb-2">Edad</label>
        <input
          type="text"
          id="age"
          name="age"
          value={pet.age}
          onChange={handleChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        />
      </div>

      <div>
        <label htmlFor="status" className="block text-gray-700 text-sm font-bold mb-2">Estado de salud</label>
        <select
          id="status"
          name="status"
          value={pet.status}
          onChange={handleChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        >
          <option value="">Seleccione el estado</option>
          {Object.values(PET_STATUS).map(status => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="description" className="block text-gray-700 text-sm font-bold mb-2">Descripción</label>
        <textarea
          id="description"
          name="description"
          value={pet.description}
          onChange={handleChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          rows="3"
          required
        ></textarea>
      </div>

      <div>
        <label htmlFor="notes" className="block text-gray-700 text-sm font-bold mb-2">Notas adicionales</label>
        <textarea
          id="notes"
          name="notes"
          value={pet.notes}
          onChange={handleChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          rows="2"
        ></textarea>
      </div>

      <div>
        <label htmlFor="location" className="block text-gray-700 text-sm font-bold mb-2">Ubicación</label>
        <input
          type="text"
          id="location"
          name="location"
          value={pet.location}
          onChange={handleChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm font-bold mb-2">Imágenes (máximo 4)</label>
        <input 
          type="file" 
          accept="image/*" 
          multiple 
          ref={fileInputRef}
          onChange={handleImageChange}
          className="hidden"
        />
        <button 
          type="button" 
          onClick={() => fileInputRef.current.click()}
          className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
        >
          Agregar Imagen
        </button>
        <div className="flex flex-wrap mt-2">
          {imagePreviews.map((preview, index) => (
            <div key={index} className="relative m-1">
              <img src={preview} alt={`Preview ${index}`} className="w-20 h-20 object-cover rounded" />
              <button
                type="button"
                onClick={() => handleRemoveImage(index)}
                className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
              >
                X
              </button>
            </div>
          ))}
        </div>
      </div>



      <button 
        type="submit" 
        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
      >
        {petId ? "Guardar cambios" : "Agregar mascota"}
      </button>
    </form>
  );
};

export default AdminForm;